/**
 * Addresses on the portal that the desktop app links out to.
 *
 * Shared so the two sides cannot drift apart: the desktop app ships on its own
 * release cadence, so a page renamed on the portal without the app following
 * leaves a link in the wizard 404ing silently, with nothing in either build to
 * catch it. One constant per address, imported by both.
 *
 * This is the portal's *public* origin, which is fixed. The API worker's view
 * of it is a separate, environment-configurable thing (`PORTAL_ORIGIN`), since
 * that one has to be localhost during development.
 */

/** Where the portal is published. */
export const PORTAL_URL = "https://bigbearbackup.com";

/** The route segment for the Backblaze setup guide, under `/help/`. */
export const BACKBLAZE_GUIDE_SLUG = "backblaze-b2";

/**
 * The anchor of the Backblaze guide's bucket section, by the id
 * `github-slugger` gives that heading in `backblaze-b2.md`.
 *
 * That section is where the guide says to leave Object Lock **Disabled**, and
 * why — so it is what the setup forms' Object Lock warning points at.
 * `HelpGuide.test.tsx` pins this string against the rendered page, so a
 * heading reworded there without updating here fails loudly instead of
 * quietly 404ing an in-page link.
 */
export const BACKBLAZE_GUIDE_ANCHOR_BUCKET = "create-a-private-bucket";

/** The route segment for the Amazon S3 setup guide, under `/help/`. */
export const S3_GUIDE_SLUG = "amazon-s3";

/**
 * The anchor of each step in the Amazon S3 guide, by the id `github-slugger`
 * gives that step's heading in `amazon-s3.md`, in the order the guide takes
 * them. The desktop's first-time checklist links to a step by one of these.
 * `HelpGuide.test.tsx` pins every one against the rendered page, so a heading
 * reworded there fails loudly instead of quietly breaking a link.
 */
export const S3_GUIDE_ANCHORS = {
  account: "create-an-aws-account",
  mfa: "turn-on-mfa-for-the-root-user",
  budgetAlert: "set-up-a-budget-alert",
  bucket: "create-the-bucket",
  iamUser: "create-an-iam-user-with-access-to-that-one-bucket",
  accessKey: "create-the-access-key",
  storageTier: "choose-a-storage-tier",
  newVault: "pick-amazon-s3-in-new-vault",
} as const;

/** The route segment for the guide about the two secrets that open a vault,
 * under `/help/`. Linked from every field in the app that handles one. */
export const SECRETS_GUIDE_SLUG = "recovery-key-and-passphrase";

/** The route segment for the "delete a vault by hand" guide, under `/help/`.
 * Linked from the desktop app's Delete dialog, by one of the anchors in
 * `vaultDeleteProviderNote.ts`. */
export const DELETE_VAULT_GUIDE_SLUG = "delete-a-vault-by-hand";

/** The route segment for the Google Cloud Storage setup guide, under
 * `/help/`. */
export const GOOGLE_CLOUD_GUIDE_SLUG = "google-cloud-storage";

/**
 * The anchor of each step in the Google Cloud Storage guide, by the id
 * `github-slugger` gives its heading in `google-cloud-storage.md`, in the
 * order the guide takes them, for the desktop setup checklist to link to.
 * `HelpGuide.test.tsx` pins each against the rendered page.
 */
export const GOOGLE_CLOUD_GUIDE_ANCHOR_ACCOUNT = "create-a-google-cloud-account-and-project";
export const GOOGLE_CLOUD_GUIDE_ANCHOR_BUDGET_ALERT = "set-up-a-budget-alert";
export const GOOGLE_CLOUD_GUIDE_ANCHOR_BUCKET = "create-the-bucket";
export const GOOGLE_CLOUD_GUIDE_ANCHOR_SERVICE_ACCOUNT =
  "create-a-service-account-with-access-to-that-bucket-only";
export const GOOGLE_CLOUD_GUIDE_ANCHOR_HMAC_KEY = "create-an-hmac-key";
export const GOOGLE_CLOUD_GUIDE_ANCHOR_STORAGE_TIERS = "storage-tiers";
export const GOOGLE_CLOUD_GUIDE_ANCHOR_NEW_VAULT = "pick-google-cloud-storage-in-big-bear";

/** The route segment for the Azure Blob Storage setup guide, under `/help/`. */
export const AZURE_GUIDE_SLUG = "azure-blob-storage";

/**
 * The anchor of each step in the Azure Blob Storage guide, by the id
 * `github-slugger` gives that step's heading in `azure-blob-storage.md`, in
 * the order the guide takes them. The desktop's first-time checklist links to
 * a step by one of these, and `HelpGuide.test.tsx` pins every one against the
 * rendered page.
 */
export const AZURE_GUIDE_ANCHORS = {
  account: "create-an-azure-account",
  budgetAlert: "set-up-a-budget-alert",
  storageAccount: "create-a-general-purpose-v2-storage-account-with-lrs",
  container: "create-the-container",
  dataProtection: "leave-blob-versioning-off-and-soft-delete-at-its-default",
  sasToken: "create-a-sas-token-for-that-container",
  storageTier: "choose-a-storage-tier",
  newVault: "pick-azure-blob-storage-in-new-vault",
} as const;

/** The full address of a Help guide, from its slug — the whole link, so a
 * caller never has to write half of it out again. */
export function helpGuideUrl(slug: string): string {
  return `${PORTAL_URL}/help/${slug}`;
}
